import type { BinaryPath, TreePath } from './tree.model';

// ── Binomial math utilities ──────────────────────────────

/** Binomial coefficient C(n, k). */
export function comb(n: number, k: number): number {
  if (k < 0 || k > n) {
    return 0;
  }
  if (k === 0 || k === n) {
    return 1;
  }
  let result = 1;
  for (let i = 0; i < Math.min(k, n - k); i++) {
    result = (result * (n - i)) / (i + 1);
  }
  return Math.round(result);
}

const SUPERSCRIPTS: Record<string, string> = {
  '0': '\u2070',
  '1': '\u00B9',
  '2': '\u00B2',
  '3': '\u00B3',
  '4': '\u2074',
  '5': '\u2075',
  '6': '\u2076',
  '7': '\u2077',
  '8': '\u2078',
  '9': '\u2079',
};

/** Convert a number to superscript unicode. */
export function sup(n: number): string {
  return String(n)
    .split('')
    .map((ch) => SUPERSCRIPTS[ch] ?? ch)
    .join('');
}

/** Generate all 2^n binary paths of length n. */
export function generatePaths(n: number): BinaryPath[] {
  if (n === 0) {
    return [[]];
  }
  const paths: BinaryPath[] = [];
  const total = 1 << n;
  for (let i = 0; i < total; i++) {
    const path: Array<'a' | 'b'> = [];
    for (let bit = n - 1; bit >= 0; bit--) {
      path.push((i >> bit) & 1 ? 'b' : 'a');
    }
    paths.push(path);
  }
  return paths;
}

/** Count the number of 'b' choices in a path. */
export function countB(path: BinaryPath): number {
  return path.filter((c) => c === 'b').length;
}

/** Convert a path to its LaTeX representation, e.g. ['a','a','b'] → "a^{2}b". */
export function pathToLatex(path: BinaryPath): string {
  const n = path.length;
  const bCount = countB(path);
  const aCount = n - bCount;
  if (n === 0) {
    return '1';
  }
  let tex = '';
  if (aCount > 0) {
    tex += aCount > 1 ? `a^{${aCount}}` : 'a';
  }
  if (bCount > 0) {
    tex += bCount > 1 ? `b^{${bCount}}` : 'b';
  }
  return tex || '1';
}

/** Convert (n, k) to the full term LaTeX with coefficient, e.g. "3a^{2}b". */
export function termToLatex(n: number, k: number): string {
  const c = comb(n, k);
  const aExp = n - k;
  const bExp = k;
  if (aExp === 0 && bExp === 0) {
    return '1';
  }
  let tex = '';
  if (c !== 1 || (aExp === 0 && bExp === 0)) {
    tex += `${c}`;
  }
  if (aExp > 0) {
    tex += aExp > 1 ? `a^{${aExp}}` : 'a';
  }
  if (bExp > 0) {
    tex += bExp > 1 ? `b^{${bExp}}` : 'b';
  }
  return tex || '1';
}

/** Convert a path to its term string with unicode, e.g. ['a','a','b'] → "a²b". */
export function pathToTerm(path: BinaryPath): string {
  const n = path.length;
  const bCount = countB(path);
  const aCount = n - bCount;
  if (n === 0) {
    return '1';
  }
  let term = '';
  if (aCount > 0) {
    term += 'a';
    if (aCount > 1) {
      term += sup(aCount);
    }
  }
  if (bCount > 0) {
    term += 'b';
    if (bCount > 1) {
      term += sup(bCount);
    }
  }
  return term || '1';
}

/** Build the full expansion string for (a+b)^n. */
export function buildExpansion(n: number): string {
  if (n === 0) {
    return '1';
  }
  const terms: string[] = [];
  for (let k = 0; k <= n; k++) {
    const c = comb(n, k);
    const aExp = n - k;
    const bExp = k;
    let term = '';
    if (c !== 1 || (aExp === 0 && bExp === 0)) {
      term += c;
    }
    if (aExp > 0) {
      term += 'a';
      if (aExp > 1) {
        term += sup(aExp);
      }
    }
    if (bExp > 0) {
      term += 'b';
      if (bExp > 1) {
        term += sup(bExp);
      }
    }
    terms.push(term);
  }
  return terms.join(' + ');
}

// ── Path conversion ──────────────────────────────────────

/** Convert a BinaryPath ('a'/'b') to a TreePath (0/1). */
export function binaryPathToTreePath(path: BinaryPath): TreePath {
  return path.map((c) => (c === 'a' ? 0 : 1));
}

/** Convert a TreePath (0/1) to a BinaryPath (only valid for binary trees). */
export function treePathToBinaryPath(path: TreePath): BinaryPath {
  return path.map((i) => (i === 0 ? 'a' : 'b'));
}
